import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import {
  Button, FeedbackStepper, Header, Tooltip, Survey,
} from '@/pages/common';
import {
  NoRefund, WithRefund, Review, OrderProcessing, SignatureConfirmation,
} from '@/components/billing';
import Loader from '@/components/Layout/Loader';
import { IconArrowWardsLeft } from '@/icons';
import { SubscriptionsQuestionTypes } from '@/redux/modules/billing/billing.types';
import { locale } from '@/utils/locale';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { formatDate } from '@/utils/Formatters/formatDate';
import { COUNTRY } from '@/config';
import { CANCEL_SUBSCRIPTION_STEPS } from '@/enums/billing/billing.enum';
import {
  subscriptionLocale,
  refundModalLocale,
  signatureConfirmationLocale,
} from './CancelSubscriptionHandler.locale';
import { billingActions, commonActions, preChatActions } from '@/redux/modules';
import createFunctions from './CancelSubscriptionHandler.functions';
import * as Styles from './CancelSubscriptionHandler.styles';
import { billingAnalytics } from '@/analytics';
import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import {
  NEW_SUPPORT, XRAY_ENABLED_RETENTION_OFFER, XRAY_ENABLE_CANCELL_WITH_SUPPORT,
} from '@/config/GrowthBook/constants';
import useLocale from '@/hooks/useLocale';
import { RetentionOfferPage } from '@/pages/billing/subscriptions/RetentionOfferPage';
import { getServerTime } from '@/utils/getServerTime/getServerTime';
import mountZendeskTags from '../../../Buttons/Prechat/utils/mountZendeskTags';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';

const CancelSubscriptionHandler = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const params = useParams();
  const subscriptionId = Number(params.subscriptionId);
  const { subscriptionType } = params;

  const summaryState = useSelector(state => state.summary);
  const loading = useSelector(state => state.common.loading);
  const subscriptions = useSelector(state => state.billing.subscriptions.items);
  const questions = useSelector(state => state.billing.subscriptions.questions);
  const cancel = useSelector(state => state.billing.subscriptions.cancel);
  const { triage } = useSelector(state => state.preChat);
  const [subscription, setSubscription] = useState(undefined);
  const { preChat: preChatLocale, billing: billingLocale } = useLocale();
  const enabledRetentionOffer = useFeatureIsOn(XRAY_ENABLED_RETENTION_OFFER);
  const enabledCancelWithSupport = useFeatureIsOn(XRAY_ENABLE_CANCELL_WITH_SUPPORT);
  const growthbook = useGrowthBook();
  const [isBusinessHours, setIsBusinessHours] = useState(false);
  const useNewSupport = useFeatureIsOn(NEW_SUPPORT);

  const [step, setStep] = useState(CANCEL_SUBSCRIPTION_STEPS.SURVEY);
  const { subscriptions: subscriptionAnalytics } = billingAnalytics;

  const canShowBackupLink = () => {
    if (subscription && subscription.showFilesExclusionText !== undefined) {
      return subscription.showFilesExclusionText;
    }

    return (subscriptionType !== 'addon' && subscriptionType !== 'domain');
  };

  const canShowCancelExclusionAddonVPSLabel = () => {
    const isVpsOCIAddon = HgProducts.VPS_OCI_ADDONS.includes(subscription.addonId);
    return isVpsOCIAddon;
  };

  const isAddonVPSOci = useCallback(() => subscription && HgProducts.VPS_OCI_ADDONS && HgProducts.VPS_OCI_ADDONS.includes(subscription.addonId), [subscription]);
  const shouldShowForwardButton = useCallback(() => !isAddonVPSOci() || step !== CANCEL_SUBSCRIPTION_STEPS.CONFIRMATION, [isAddonVPSOci, step]);

  const alterAttributeInGrowthbook = useCallback(() => {
    if (growthbook && subscription && subscription.productName) {
      growthbook.setAttributes({
        ...growthbook.getAttributes(),
        plano_id: subscription.productName,
      });
    }
  }, [growthbook, subscription]);

  useEffect(() => {
    alterAttributeInGrowthbook();
  }, [alterAttributeInGrowthbook]);

  const isWithinBusinessHours = () => {
    const { formattedTime } = getServerTime();
    const now = new Date(formattedTime);
    const dayOfWeek = now.getDay();
    const hour = now.getHours();

    return dayOfWeek >= 1 && dayOfWeek <= 5 && hour >= 8 && hour < 18;
  };

  useEffect(() => {
    setIsBusinessHours(isWithinBusinessHours());
  }, [enabledCancelWithSupport]);

  const onClickBackupLink = () => {
    subscriptionAnalytics.cancelSubscriptionClick('6_link_veja_como_fazer_backup_cancelar_imediato');
    window.open(subscriptionLocale.backupFilesLinkUrl, '_blank');
  };

  const {
    questionId, optionId, reason, modal,
  } = cancel;

  const openPreChatCancelFlow = useCallback(() => {
    if (triage.subscriptionToCancel) {
      dispatch(preChatActions.preChat.setShowPreChat(true));
      dispatch(preChatActions.subscription.setSubscriptionToCancel(null));
    }
  }, [triage, dispatch]);

  const { values, methods } = createFunctions({
    dispatch,
    history,
    questions,
    setStep,
    setSubscription,
    step,
    subscriptionId: Number(subscriptionId),
    subscriptions,
    subscriptionType,
    openPreChatCancelFlow,
    enabledRetentionOffer,
    optionId,
  });

  const {
    steps,
  } = values;

  const {
    getPaymentOptions,
    handleOnOptionSelected,
    handleOnReasonChange,
    handleOnRefundPoliticsClick,
    handleOnStopCancel,
    onFowardStepClick,
    onPreviousStepClick,
    requestQuestionsEffect,
    resetSurveyEffect,
  } = methods;

  const handleDeclineRenewDiscount = useCallback(() => {
    setStep(CANCEL_SUBSCRIPTION_STEPS.REFUND);
  }, []);

  const handleAnswer = useCallback(() => {
    const actionSuccess = () => {
      setStep(CANCEL_SUBSCRIPTION_STEPS.NEXT_RENEW_OFFER);
    };

    const actionFail = () => {
      onFowardStepClick();
    };

    if (enabledRetentionOffer && step < CANCEL_SUBSCRIPTION_STEPS.REFUND) {
      dispatch(billingActions.subscriptions.ableToCancelOffer.request(
        subscriptionId, subscriptionType, optionId, actionSuccess, actionFail,
      ));
    } else {
      onFowardStepClick();
    }
  }, [
    dispatch,
    enabledRetentionOffer,
    optionId,
    onFowardStepClick,
    step,
    subscriptionId,
    subscriptionType,
  ]);

  useEffect(() => {
    subscriptionAnalytics.cancelSubscriptionPageView('3_pesquisa_respondida_cancelar_imediato');
  }, [subscriptionAnalytics]);

  const getCancelledAddonsTopics = () => {
    if (subscription && subscription.addons) {
      return subscription.addons.map(addon => signatureConfirmationLocale.addonCancellationAlert(addon));
    }

    return [];
  };

  const {
    exampleCalculation,
    amountOwed,
    amountAvailableRefund,
  } = refundModalLocale.accordionCancelledAfterContent;

  const countriesCurrencyValues = {
    mx: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    cl: {
      CONTRACT_VALUE: '17637,84',
      REFUND_VALUE: '2057,04',
      REMAINING_VALUE: '2938,62',
      USED_VALUE: '14693,11',
    },
    co: {
      CONTRACT_VALUE: '84105,60',
      REFUND_VALUE: '9819,60',
      REMAINING_VALUE: '14028,00',
      USED_VALUE: '70140,00',
    },
    ar: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    bo: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    cr: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    do: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    ec: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    gt: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    pa: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    pe: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    py: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    sv: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
    uy: {
      CONTRACT_VALUE: '442,31',
      REFUND_VALUE: '51,60',
      REMAINING_VALUE: '73,72',
      USED_VALUE: '368,59',
    },
  };

  if (COUNTRY !== 'br') {
    exampleCalculation.text = exampleCalculation.text
      .replace(/(\[CONTRACT_VALUE\])/, formatCurrency(countriesCurrencyValues[COUNTRY].CONTRACT_VALUE))
      .replace(/(\[REMAINING_VALUE\])/, formatCurrency(countriesCurrencyValues[COUNTRY].REMAINING_VALUE))
      .replace(/(\[REFUND_VALUE\])/, formatCurrency(countriesCurrencyValues[COUNTRY].REFUND_VALUE));

    amountOwed.annualContractAmount = amountOwed.annualContractAmount
      .replace(/(\[CONTRACT_VALUE\])/, formatCurrency(countriesCurrencyValues[COUNTRY].CONTRACT_VALUE));

    amountOwed.usedAmount = amountOwed.usedAmount
      .replace(/(\[USED_VALUE\])/, formatCurrency(countriesCurrencyValues[COUNTRY].USED_VALUE));

    amountAvailableRefund.totalAmount = amountAvailableRefund.totalAmount
      .replace(/(\[REMAINING_VALUE\])/, formatCurrency(countriesCurrencyValues[COUNTRY].REMAINING_VALUE));

    refundModalLocale.accordionCancelledAfterContent = {
      ...refundModalLocale.accordionCancelledAfterContent,
      exampleCalculation,
      amountOwed,
      amountAvailableRefund,
    };
  }

  const isSurveyStepComplete = useCallback(() => !questionId || !optionId, [questionId, optionId]);

  const onRefundMethodSelected = (refundMethod) => {
    dispatch(billingActions.subscription.refund.method.set(
      refundMethod,
      subscriptionId,
      subscriptionType,
    ));
  };

  const getSubscriptionName = () => {
    if (subscription) {
      const { type } = subscription;
      return type === 'domain'
        ? `${billingLocale.domainLabel}: ${subscription.domain}`
        : subscription.productName;
    }

    return '';
  };

  const openChatWithSupport = useCallback(async () => {
    const { id } = summaryState;
    const subscriptionInfoBySupport = JSON.parse(localStorage.getItem('subscriptionInfoBySupport')) || null;
    const reasonSupport = (subscriptionInfoBySupport && subscriptionInfoBySupport.reasonSupport) || '';
    const productId = (subscriptionInfoBySupport && subscriptionInfoBySupport.product_id) || '';

    const tags = await mountZendeskTags({
      department: 'financial',
      primaryReason: reasonSupport,
      secondaryReason: 'cancel-with-support',
      product: productId,
      whmcsId: id,
      departmentLocales: preChatLocale.zopimDepartments,
      v2: useNewSupport,
    });

    // eslint-disable-next-line no-undef
    zE('messenger:set', 'conversationTags', tags);
    await new Promise(resolve => setTimeout(resolve, 500));

    // eslint-disable-next-line no-undef
    zE('messenger', 'open');
  }, [
    summaryState,
    preChatLocale,
    useNewSupport,
  ]);

  const stepsStatus = {
    SURVEY: 1,
    CONFIRMATION: 2,
    DONE: 3,
  };

  const verifyParamsInUrl = useCallback(() => {
    if (enabledRetentionOffer) {
      const containsStep = window.location.href.includes('step=');
      const step = window.location.href.includes('step=2') ? 2 : 1;
      const shouldOpenInvoice = containsStep && step === 2;

      if (shouldOpenInvoice) {
        setStep(stepsStatus.CONFIRMATION);
      }
    }
  }, [setStep, enabledRetentionOffer, stepsStatus.CONFIRMATION]);

  useEffect(() => {
    verifyParamsInUrl();
  }, [verifyParamsInUrl]);

  useEffect(() => {
    if (!subscriptions.length) {
      dispatch(billingActions.subscriptions.request());
    }
  }, [subscriptions, dispatch]);

  useEffect(() => {
    if (subscriptions.length) {
      subscriptions
        .filter(({ id, type }) => id === subscriptionId && type === subscriptionType)
        .forEach(item => setSubscription(item));
    }
  }, [subscriptionId, subscriptionType, subscriptions]);

  useEffect(() => {
    requestQuestionsEffect();
    resetSurveyEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (subscription && !subscription.refund) {
      dispatch(billingActions.subscription.refund.check.request({
        subscriptionId,
        subscriptionType,
      }));
    }
  }, [subscription, subscriptionId, subscriptionType, dispatch]);

  useEffect(() => {
    modal && history.push(`${locale('routes.subscriptions')}`);
  }, [history, modal]);

  useEffect(() => {
    dispatch(commonActions.serverTime.request());
  }, [dispatch]);

  if (loading
    || !subscription
    || (subscription && !subscription.refund)
    || !questions[SubscriptionsQuestionTypes.CANCEL_SIGNATURE]
  ) {
    return <Loader />;
  }

  return (
    <Styles.Wrapper data-testid="subscription-cancel-handler">
      <Styles.Header>
        <Header
          title={
            `${subscription.type === 'domain'
              ? billingLocale.domainLabel
              : subscription.productName} - `
          }
          description={subscription.domain}
        />
      </Styles.Header>
      <Styles.Content>
        <FeedbackStepper
          activeStep={step}
          steps={steps}
        />
        <Styles.StepContent>
          {step === CANCEL_SUBSCRIPTION_STEPS.NEXT_RENEW_OFFER && (
            <RetentionOfferPage handleDeclineRenewDiscount={handleDeclineRenewDiscount} />
          )}
          {
            step === CANCEL_SUBSCRIPTION_STEPS.SURVEY && (
              <Styles.StepSurveyContainer data-testid="subscription-cancel-signature-step-survey">
                <Survey
                  questions={questions[SubscriptionsQuestionTypes.CANCEL_SIGNATURE]}
                  questionId={questionId}
                  optionId={optionId}
                  reason={reason}
                  testId="subscription-cancel-signature-survey"
                  onOptionSelected={handleOnOptionSelected}
                  onReasonChange={handleOnReasonChange}
                  onClickSupport={openChatWithSupport}
                  enabledCancelWithSupport={isBusinessHours && enabledCancelWithSupport}
                  cancelWithSupportTitle={subscriptionLocale.cancelWithSupportTitle}
                  cancelWithSupportSubTitle={subscriptionLocale.cancelWithSupportSubTitle}
                  cancelWithSupportTalk={subscriptionLocale.cancelWithSupportTalk}
                  cancelWithSupportButton={subscriptionLocale.cancelWithSupportButton}
                  questionDescriptionLabel={subscriptionLocale.surveyQuestionDescriptionLabel}
                  reasonQuestionLabel={subscriptionLocale.surveyReasonQuestionLabel}
                />
              </Styles.StepSurveyContainer>
            )
          }
          {
            step === CANCEL_SUBSCRIPTION_STEPS.REFUND && (
              <Styles.StepConfirmationContainer data-testid="subscription-cancel-signature-step-refund">
                {(subscription.refund && subscription.refund.total > 0)
                  ? (
                    <WithRefund
                      title={subscriptionLocale.withRefund.title}
                      totalLabel={subscriptionLocale.withRefund.totalLabel}
                      refundPolitics={subscriptionLocale.withRefund.refundPolitics}
                      onRefundPoliticsClick={handleOnRefundPoliticsClick}
                      tipTitle={subscriptionLocale.withRefund.tipTitle}
                      tip={subscriptionLocale.withRefund.tipText(subscription ? formatDate(subscription.dueDate) : null)}
                      stopCancelLabel={subscriptionLocale.withRefund.giveUp}
                      handleStopCancel={handleOnStopCancel}
                      selectMethodTitle={subscriptionLocale.withRefund.selectMethodTitle}
                      onChangeSelected={onRefundMethodSelected}
                      methodOptions={getPaymentOptions()}
                      refund={subscription.refund}
                    />
                  )
                  : (
                    <NoRefund
                      amountTitle={subscriptionLocale.noRefund.amountTitle}
                      withoutRefund={subscriptionLocale.noRefund.withoutRefund}
                      refundPolitics={subscriptionLocale.noRefund.refundPolitics}
                      tipTitle={subscriptionLocale.noRefund.tipTitle}
                      tipText={subscriptionLocale.noRefund.tipText}
                      tipDate={formatDate(subscription.dueDate)}
                      stopCancelLabel={subscriptionLocale.noRefund.stopCancelLabel}
                      handleStopCancel={handleOnStopCancel}
                      onRefundPoliticsClick={handleOnRefundPoliticsClick}
                    />
                  )
                }
              </Styles.StepConfirmationContainer>
            )
          }
          {
            step === CANCEL_SUBSCRIPTION_STEPS.REVIEW && (
              <Styles.StepConfirmationContainer data-testid="subscription-cancel-signature-step-review">
                <Review
                  title={subscriptionLocale.review.title}
                  signature={getSubscriptionName()}
                  signatureTitle={subscriptionLocale.review.signatureTitle}
                  reasonTitle={subscriptionLocale.review.reasonTitle}
                  reasonOption={cancel.questionValue}
                  reasonDescription={cancel.reason}
                  refundTitle={subscriptionLocale.review.refundTitle}
                  totalLabel={subscriptionLocale.review.totalLabel}
                  methodOptions={getPaymentOptions()}
                  refund={subscription.refund}
                />
              </Styles.StepConfirmationContainer>
            )
          }

          {
            (step === CANCEL_SUBSCRIPTION_STEPS.CONFIRMATION) && (
              <Styles.StepConfirmationContainer data-testid="subscription-cancel-signature-step-confirmation">
                <SignatureConfirmation
                  title={subscriptionLocale.confirmationTitleCancelSignatureLabel}
                  subscriptions={subscriptions}
                  subscription={subscription}
                  handleAnswer={handleAnswer}
                  isAddonVPSOci={isAddonVPSOci}
                  attentionLabel={subscriptionLocale.attentionLabel}
                  canShowBackupLink={canShowBackupLink()}
                  backupFilesLabel={subscriptionLocale.backupFilesLabel}
                  backupFilesLinkLabel={subscriptionLocale.backupFilesLinkLabel}
                  onClickBackupLink={onClickBackupLink}
                  onStopCancel={handleOnStopCancel}
                  stopCancelLabel={subscriptionLocale.stopCancelSignatureLabel}
                  attentionCancelExclusionLabel={canShowCancelExclusionAddonVPSLabel() && subscriptionLocale.attentionCancelExclusionLabel}
                  attentionCancelSignatureLabel={canShowBackupLink() ? signatureConfirmationLocale.attentionCancelSignature : signatureConfirmationLocale.attentionCancelSignatureAddon}
                  topics={[
                    ...getCancelledAddonsTopics(),
                  ]}
                />
              </Styles.StepConfirmationContainer>
            )
          }
          {
            step === CANCEL_SUBSCRIPTION_STEPS.DONE && (
              <Styles.StepDoneContainer data-testid="subscription-cancel-signature-step-done">
                <OrderProcessing
                  text={subscriptionLocale.processing}
                />
              </Styles.StepDoneContainer>
            )
          }
        </Styles.StepContent>

        {step !== CANCEL_SUBSCRIPTION_STEPS.NEXT_RENEW_OFFER && (
          <Styles.Controls disabled={step === CANCEL_SUBSCRIPTION_STEPS.DONE}>
            {
              step !== CANCEL_SUBSCRIPTION_STEPS.DONE && (
                <>
                  <Styles.ControlLeft>
                    <Button
                      label={subscriptionLocale.controlBackButtonLabel.toUpperCase()}
                      iconLeft={<IconArrowWardsLeft />}
                      onClick={onPreviousStepClick}
                      testId="subscription-cancel-signature-back-button"
                    />
                  </Styles.ControlLeft>
                  {shouldShowForwardButton() && (
                    <Styles.ControlRight>
                      <Tooltip
                        width="270"
                        position="left"
                        text={subscriptionLocale.tooltipCancelSignatureText}
                        disabled={!isSurveyStepComplete()}
                      >
                        <Button
                          disabled={isSurveyStepComplete() || !subscription.refund}
                          iconRight={step === CANCEL_SUBSCRIPTION_STEPS.CONFIRMATION ? null : <IconArrowWardsLeft />}
                          iconRightDirection="right"
                          label={
                            (step === CANCEL_SUBSCRIPTION_STEPS.CONFIRMATION
                              ? subscriptionLocale.controlFowardButtonStepConfirmationCancelSignatureLabel
                              : subscriptionLocale.controlFowardButtonLabel).toUpperCase()
                          }
                          onClick={handleAnswer}
                          testId="subscription-cancel-signature-foward-button"
                          variant="primary"
                        />
                      </Tooltip>
                    </Styles.ControlRight>
                  )}
                </>
              )
            }
          </Styles.Controls>
        )}
      </Styles.Content>
    </Styles.Wrapper>
  );
};

export default CancelSubscriptionHandler;
